import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';

import { fetchWhitelistInterest, promoteWhitelistInterest } from '../../../api/adminWhitelist';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { SearchInput } from '../../../components/ui/search-input';
import { Spinner } from '../../../components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useNotification } from '../../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../../lib/errors';

const PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export function WhitelistInterestTab() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'whitelist-interest', { q: search, page }],
    queryFn: () => fetchWhitelistInterest({ q: search || undefined, page, page_size: PAGE_SIZE }),
    placeholderData: (previous) => previous,
  });

  const promoteMutation = useMutation({
    mutationFn: (id: string) => promoteWhitelistInterest(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-interest'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-emails'] });
      showSuccess(`${result.email} adicionado à whitelist.`);
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          value={search}
          onChange={(value) => {
            setPage(1);
            setSearch(value);
          }}
          placeholder="Buscar por email..."
          className="flex-1 max-w-sm"
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && !data ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size={32} label="Carregando interessados" />
            </div>
          ) : data && data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--color-text-secondary)]">
              Ninguém registrou interesse ainda.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="hidden sm:table-cell">IP</TableHead>
                  <TableHead className="hidden lg:table-cell">User-Agent</TableHead>
                  <TableHead className="hidden md:table-cell">Registrado em</TableHead>
                  <TableHead className="w-48" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium break-all">
                      <div className="flex flex-col gap-0.5">
                        <span>{item.email}</span>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                          {item.ip && <span>{item.ip}</span>}
                          <span>{formatDateTime(item.created_at)}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-[var(--color-text-secondary)]">
                      {item.ip ?? '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-[var(--color-text-secondary)]">
                      {item.user_agent ? (
                        <span className="break-all">{item.user_agent}</span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-[var(--color-text-tertiary)]">
                      {formatDateTime(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => promoteMutation.mutate(item.id)}
                        disabled={promoteMutation.isPending}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Adicionar à whitelist
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)]">
          <span>
            Página {data.page} de {totalPages} ({data.total} interessados)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
